import PropTypes from 'prop-types';
import React from 'react';
import { Table } from 'react-bootstrap';
import Time from 'react-time';
import { getOrgTermsContent } from '../../content/utils.js';
import { RATINGS } from '../../lib/schema.js';
import { ProjectLink } from '../links/index.jsx';
import RatingLabel from '../RatingLabel.jsx';
import OwnersList from '../OwnersList.jsx';
import DatesProgressBar from './DatesProgressBar.jsx';
import ProjectActionDropdown from './ProjectActionDropdown.jsx';
import { calculatePadding } from '../../constants/index.js';
import { duplicationText } from '../../constants/projectDuplication.js';
import styles from './ProjectsTable.module.css';

const ProjectsTableRow = (
  project,
  statuses,
  onDeleteProject,
  indentLevel = 0
) => {
  const {
    id,
    name,
    status,
    startDate,
    endDate,
    owners,
    impact,
    scope,
    projectStatusId,
    duplicating,
  } = project;

  const renderStatus = () => {
    if (duplicating) {
      return (
        <span className={`flex text-muted ${styles.duplicating}`}>
          <span className={styles.spinner} />
          {duplicationText('duplicating')}
        </span>
      );
    }

    const statusObj = statuses.find((s) => s.id === projectStatusId);
    if (!statusObj || statusObj.archived)
      return <span className="text-muted">{status}</span>;

    return status;
  };

  return (
    <tr key={id} className={duplicating ? styles.duplicatingRow : undefined}>
      <td
        style={{
          paddingLeft: calculatePadding(indentLevel),
        }}
      >
        <h3>
          {/* The copy cannot be opened until the duplication is finished */}
          {duplicating ? (
            <span className="text-muted">{name}</span>
          ) : (
            <ProjectLink projectId={id}>
              <a>{name}</a>
            </ProjectLink>
          )}
        </h3>
      </td>
      <td>
        <strong>{renderStatus()}</strong>
      </td>
      <td>
        <strong>
          <RatingLabel rating={scope} reverse />
        </strong>
      </td>
      <td>
        <strong>
          <RatingLabel rating={impact} reverse />
        </strong>
      </td>
      <td>
        {endDate && startDate ? (
          <div className="text-center">
            <div className="m-b-4">
              <Time value={startDate} format="M/D/YY" />
              &nbsp; - &nbsp;
              <Time value={endDate} format="M/D/YY" />
            </div>
            <DatesProgressBar startDate={startDate} endDate={endDate} />
          </div>
        ) : (
          '—'
        )}
      </td>

      <td>
        <OwnersList owners={owners} nameKey="fullName" row={true} />
      </td>

      <td style={{ textAlign: 'right' }}>
        {duplicating ? null : (
          <ProjectActionDropdown
            projectId={id}
            project={project}
            onDeleteProject={onDeleteProject}
          />
        )}
      </td>
    </tr>
  );
};

const ProjectsTable = ({
  projects,
  statuses,
  onDeleteProject,
  indentLevel = 0,
}) => {
  return (
    <Table hover>
      <thead>
        <tr>
          <th
            style={{
              width: '28%',
              paddingLeft: calculatePadding(indentLevel),
            }}
          >
            {getOrgTermsContent('projectName')}
          </th>
          <th style={{ width: '15%' }}>Status</th>
          <th style={{ width: '10%' }}>Scope</th>
          <th style={{ width: '10%' }}>Impact</th>
          <th style={{ width: '16px' }}>Dates</th>
          <th style={{ width: '14%' }}>Owner</th>
          <th style={{ width: '7%', textAlign: 'end' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {projects.map((project) =>
          ProjectsTableRow(project, statuses, onDeleteProject, indentLevel)
        )}
      </tbody>
    </Table>
  );
};

ProjectsTable.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      startDate: PropTypes.object.isRequired,
      endDate: PropTypes.object.isRequired,
      scope: PropTypes.oneOf(RATINGS.LARGE_MEDIUM_SMALL).isRequired,
      owners: PropTypes.array.isRequired,
    })
  ).isRequired,
};

export default ProjectsTable;
